import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GAME_KIND_LABELS, accentOf, formatReleaseDate } from '@goodbuild/shared';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Eyebrow,
  GameKindBanner,
  Heading,
  Icon,
  StatusTag,
  Text,
} from '../ui/index.js';
import { useAccess, useGame } from '../api/hooks.js';
import { Focusable, FocusZone, focusTo } from '../components/focus/Focusable.js';
import { InstallButton } from '../components/game/InstallButton.js';
import { ColdOpen } from '../components/game/ColdOpen.js';
import { backdropFor } from '../components/game/HeroArt.js';
import {
  GameAbout,
  GameContentNotice,
  GameFeatures,
  GameLanguages,
  GameLegalNotice,
  GameRequirements,
} from '../components/game/GameFacts.js';
import { GameTeam } from '../components/game/GameTeam.js';
import { MediaGallery } from '../components/game/MediaGallery.js';
import { SteamDescription } from '../components/game/SteamDescription.js';
import { StoreLinks } from '../components/game/StoreLinks.js';
import { Breadcrumbs } from '../components/layout/Breadcrumbs.js';
import { useBackdrop } from '../components/layout/Wallpaper.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { useStillness } from '../lib/motion.js';
import { formatBytes, formatDateTime, formatRelative } from '../lib/format.js';
import { buildPlatform } from '../platform/index.js';

export const Route = createFileRoute('/games/$slug')({
  component: GamePage,
});

/** The anchor the shot's action jumps to, and the focus key the ring lands on when it does. */
const BUY_ID = 'gb-buy';
const BUY_FOCUS = 'game-buy';

/**
 * One game, in three acts.
 *
 * **The cold open**, full bleed, the height of most of a screen: the artwork, the trailer where
 * there is one, the title at title-card size, the build this device would get, and the one
 * action the page exists for. See `ColdOpen` for what it is for and what turns it off.
 *
 * **The reel**, in a band under it: the gallery, out of the two-column grid and given the page
 * to itself, but not run to the edges. A screenshot is 16:9, so a frame the width of the page
 * would be nine hundred pixels tall and the only way to hold that down is to crop the ratio
 * back out of it. `MediaGallery` already solves this — it takes its width from a ceiling on its
 * height — so the band is a place to put it and nothing more.
 *
 * **The store page**, which is the one this used to open on and is unchanged. The name across
 * the top became the title card, so what is left is two columns that hold for the length of the
 * page: the facts panel down the right — everything the studio can simply state, in one list
 * that is scanned rather than read — and everything else down the left, in the order the
 * questions arrive in. That argument still holds; it was only ever wrong about the first
 * screenful. Nothing sits under the panel. Everything not in it is to the left of it.
 */
function GamePage() {
  const navigate = useNavigate();
  const { slug } = Route.useParams();
  const { hasAccess } = useAccess();
  const { data: game, isLoading, isError } = useGame(slug);

  // The screen belongs to the game rather than to the shell: its artwork is behind everything,
  // blurred back by the wallpaper so nothing here is read against a picture, and its colour is
  // on the document for the cold open and the wallpaper's own wash to read.
  useBackdrop(game ? backdropFor(game) : null, game ? accentOf(game) : null);

  if (isLoading) return <Loading label="Opening the game" />;
  if (isError || !game) {
    return (
      <EmptyState
        title="That game is not here."
        body="It may have been unpublished, or the address may be wrong."
      />
    );
  }

  const playable = hasAccess;

  const build = buildPlatform ? game.builds.find((b) => b.platform === buildPlatform) : undefined;
  const released = formatReleaseDate(game.releaseDate, game.releaseNote);
  const hasMedia = game.trailers.length + game.screenshots.length > 0;

  return (
    <FocusZone focusKey="game" direction="column" className="gb-store">
      {/* Over the shot rather than above it, because a crumb row above the artwork would put a
          band of page between the top of the screen and the picture — and the picture reaching
          the top edge is most of what makes this read as an opening shot rather than a banner.
          A game is reached from the shelf, from search, from a notification and from the tray,
          so back lands somewhere different every time; this is the one way up that does not. */}
      <div className="gb-store__crumbs">
        <Breadcrumbs trail={[{ label: 'Games', link: { to: '/games' } }, { label: game.title }]} />
      </div>

      <ColdOpen
        game={game}
        build={build}
        action={playable ? <InstallButton game={game} /> : <GetAction />}
      />

      {/* The reel: the gallery with the page to itself, sized by `MediaGallery` rather than by
          the band around it. A game with nothing in its gallery has no reel at all and the store
          page begins here — the artwork is not missing from that page, it is the whole top of
          it. */}
      {hasMedia ? (
        <div className="gb-reel">
          <MediaGallery trailers={game.trailers} shots={game.screenshots} title={game.title} />
        </div>
      ) : null}

      {/* The two columns, for the whole length of the page. */}
      <FocusZone focusKey="game-columns" className="gb-store__cols">
        <div className="gb-store__main">
          {/* Between the artwork and the button that starts the download, because that is the
              last moment the warning can still change someone's mind. A finished game renders
              nothing here and the page closes up around it. */}
          <GameKindBanner kind={game.kind} />

          {/* Steam's purchase box, which is the one part of that page nobody has ever had to
              hunt for: its own frame, its own line, nothing else competing inside it. Ours holds
              a price only when the game is sold on its own; for a member, and for a dev build or
              a prototype, the same frame says what the button will cost the disk instead.

              It keeps the Play button for somebody who already has the game, even though the
              shot above has one too. They are the same button and only one of them is ever on
              screen: by the time this is in view the shot's has become the bar at the top, and
              the bar hides itself over this box so the page never shows two. */}
          <section className="gb-buy" id={BUY_ID}>
            <div className="gb-buy__head">
              <Heading size="4" render={<h2 />}>
                {playable ? `Play ${game.title}` : `Get ${game.title}`}
              </Heading>
              {build ? (
                <Text size="1" color="gray" mono className="gb-buy__meta">
                  {`${build.versionLabel} · ${formatBytes(build.totalBytes)} · pushed ${formatRelative(build.publishedAt)}`}
                </Text>
              ) : null}
            </div>

            {/* The Play button is what a membership or a purchase buys, so this is where each
                gets asked for. Owning this one game and subscribing to everything are different
                purchases rather than two prices for the same thing, and the box says so.

                Its own zone, so the shot's action has somewhere to send the ring: a jump that
                moved the page but left focus at the top would be undone by the next press of a
                direction. */}
            <FocusZone focusKey={BUY_FOCUS} direction="column">
              {playable ? <InstallButton game={game} /> : null}
            </FocusZone>
          </section>

          {/* Everything past the purchase box is reading, and reading wants one column the
              width of a paragraph. It keeps the width of the pictures above it rather than
              widening out under the panel, so the page has one left edge and one right edge all
              the way down. In Steam's order, because it is the order the questions arrive in:
              what is it, what does it need, who made it. */}
          <div className="gb-store__body">
            <GameContentNotice game={game} />

            {game.descriptionBb ? (
              <section className="gb-section">
                <Eyebrow>{'About this game'}</Eyebrow>
                {/* Steam's own BBCode, drawn as written. See `SteamDescription`. */}
                <SteamDescription source={game.descriptionBb} steamUrl={game.steamUrl} />
              </section>
            ) : null}

            <GameRequirements game={game} />

            {game.devs.length > 0 ? (
              <section className="gb-section">
                <Eyebrow>{'Made by'}</Eyebrow>
                <Card size="3" className="gb-stack gb-stack--tight">
                  {game.devs.map((dev) => (
                    <div key={dev.id} className="gb-row gb-row--tight">
                      <Avatar
                        size="2"
                        src={dev.avatarUrl ?? undefined}
                        fallback={dev.username.slice(0, 1)}
                      />
                      <Text size="2">{dev.displayName ?? dev.username}</Text>
                      <Badge color="violet">{'Dev'}</Badge>
                    </div>
                  ))}
                  <Focusable
                    onSelect={() => void navigate({ to: '/studio' })}
                    selectLabel="Meet the studio"
                    className="gb-focusable--flat"
                  >
                    <Button size="3" variant="surface" block>
                      <Icon name="studio" size={16} />
                      {'Meet the studio'}
                    </Button>
                  </Focusable>
                </Card>
              </section>
            ) : null}

            <GameLegalNotice game={game} />
          </div>
        </div>

        {/* The facts panel: everything on this page that can be stated rather than argued, in
            one list. The blurb, who made it and when, what this device would download, what can
            be done in it, what it is spoken in, and where else it lives.

            It used to open on the header capsule. The cold open is now the same artwork at
            fifteen times the size two screens above it, so the capsule was the third copy of one
            picture on one page — and it was holding the high fetch priority that the shot needs
            to be the first thing painted. The panel opens on the sentence instead, which is the
            first thing in it anybody was reading. */}
        <aside className="gb-glance">
          <Text render={<p className="gb-glance__blurb" />} size="2">
            {game.shortDescription}
          </Text>

          {/* What a glance box is for, in Steam's order: who made it, when it came out, and —
              ours, because a launcher has to say it — which build this device would get, how
              big it is, and whether there is one at all. */}
          <Card size="3" className="gb-stack gb-stack--tight">
            <Eyebrow small>{'At a glance'}</Eyebrow>
            <dl className="gb-facts gb-facts--left">
              {game.developerName ? (
                <>
                  <dt>{'Developer'}</dt>
                  <dd>{game.developerName}</dd>
                </>
              ) : null}
              {/* Only when it differs. A studio that publishes its own games would otherwise
                  have its name read back twice, which says nothing and costs a row. */}
              {game.publisherName && game.publisherName !== game.developerName ? (
                <>
                  <dt>{'Publisher'}</dt>
                  <dd>{game.publisherName}</dd>
                </>
              ) : null}
              {released ? (
                <>
                  <dt>{'Released'}</dt>
                  <dd>{released}</dd>
                </>
              ) : null}
              <dt>{'Rung'}</dt>
              <dd>{GAME_KIND_LABELS[game.kind]}</dd>
            </dl>
          </Card>

          <Card size="3" className="gb-stack gb-stack--tight">
            <Eyebrow small>{'This build'}</Eyebrow>
            {build ? (
              <dl className="gb-facts">
                <dt>{'Version'}</dt>
                <dd>{build.versionLabel}</dd>
                <dt>{'Size'}</dt>
                <dd>{formatBytes(build.totalBytes)}</dd>
                <dt>{'Pushed'}</dt>
                <dd>{formatDateTime(build.publishedAt)}</dd>
              </dl>
            ) : (
              <Text size="2" color="gray">
                {game.builds.length > 0
                  ? 'There is no build for this device yet. It exists on other platforms.'
                  : 'No build has been published yet.'}
              </Text>
            )}

            {game.builds.length > 0 ? (
              <>
                {/* "Platforms" and not "Also on", which is a hair from the "Also available on"
                    card below and was being read as the same list. */}
                <Eyebrow small tone="quiet">
                  {'Platforms'}
                </Eyebrow>
                <div className="gb-row gb-row--tight">
                  {game.builds.map((b) => (
                    <Badge key={b.platform} color={b.platform === buildPlatform ? 'green' : 'gray'}>
                      {b.platform}
                    </Badge>
                  ))}
                </div>
              </>
            ) : (
              <StatusTag kind="unavailable" />
            )}
          </Card>

          <GameAbout game={game} />

          {/* Who made it, by name, with what each of them did on this one. Directly under
              "About", which is where the developer's name is: the company and the people are
              the same question asked at two sizes, and they belong next to each other. */}
          <GameTeam game={game} />

          <GameFeatures game={game} />
          <GameLanguages game={game} />
          <StoreLinks game={game} />
        </aside>
      </FocusZone>
    </FocusZone>
  );
}

/**
 * What the shot offers somebody who cannot play this yet.
 *
 * Not the purchase box. Buying is a decision with two sides to it — this game on its own, or
 * the membership that opens every rung — and `BuyGame` is the apparatus for making it. What it
 * is not is the first thing somebody should meet on a page they opened to look at a game.
 *
 * So the shot asks the question and the box answers it: one button, carrying the price where
 * there is one, that takes the page down to the box and hands it the ring. The alternative was
 * two buy buttons on one page, which is the arrangement where nobody is sure which is the real
 * one.
 */
function GetAction() {
  const still = useStillness();

  const label = 'Get access';

  return (
    <Focusable
      onSelect={() => {
        document.getElementById(BUY_ID)?.scrollIntoView({
          behavior: still ? 'auto' : 'smooth',
          block: 'center',
        });
        // The ring goes with the page. A jump that moved the scroll and left focus on the
        // button it came from is a jump the next press of a direction would undo.
        focusTo(BUY_FOCUS);
      }}
      selectLabel={label}
    >
      {/* No icon. The purchase box below does not use one either, and the two are the same
          button at two moments in the page. */}
      <Button size="4" variant="solid">
        {label}
      </Button>
    </Focusable>
  );
}
